/**
 * Gate logic for questionnaire items: when a `gateFor` parent is answered with a
 * gating value (0 — e.g. "No" / "Never"), its linked items are hidden. Their
 * previously-entered answers must be dropped so a hidden subtree is never
 * counted downstream (scoring, positivity checks, persistence) — e.g. a C-SSRS
 * parent changed to "No" must not leave positive sub-item answers behind.
 *
 * @agentic connections
 */

export type GateableItem = {
  readonly linkId: string;
  readonly gateFor?: readonly string[];
};

/**
 * Return the set of link ids gated out (transitively) when `changedLinkId` is
 * answered with `value`. Empty unless `value === 0` and the item gates others.
 *
 * @agentic connections
 */
export const collectGatedOutLinkIds = (
  items: readonly GateableItem[],
  changedLinkId: string,
  value: number,
): Set<string> => {
  const gatedOut = new Set<string>();
  if (value !== 0) return gatedOut;

  const gateForByLinkId = new Map<string, readonly string[]>();
  for (const item of items) {
    if (item.gateFor) gateForByLinkId.set(item.linkId, item.gateFor);
  }

  const queue = [...(gateForByLinkId.get(changedLinkId) ?? [])];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (gatedOut.has(id)) continue;
    gatedOut.add(id);
    for (const child of gateForByLinkId.get(id) ?? []) queue.push(child);
  }
  return gatedOut;
};

/**
 * Given the current answers, return every link id that is hidden by gating: any
 * item with `gateFor` answered 0 hides its linked subtree (transitively). Used
 * to keep completion and progress from counting gated-out items — e.g. AUDIT-C
 * answered "Never" is complete at 0/12 without answering the hidden items 2–3.
 *
 * @agentic connections
 */
export const computeGatedHiddenLinkIds = (
  items: readonly GateableItem[],
  valueByLinkId: ReadonlyMap<string, number | undefined>,
): Set<string> => {
  const gateForByLinkId = new Map<string, readonly string[]>();
  for (const item of items) {
    if (item.gateFor) gateForByLinkId.set(item.linkId, item.gateFor);
  }

  const hidden = new Set<string>();
  for (const item of items) {
    if (!item.gateFor || valueByLinkId.get(item.linkId) !== 0) continue;
    const queue = [...item.gateFor];
    while (queue.length > 0) {
      const id = queue.shift()!;
      if (hidden.has(id)) continue;
      hidden.add(id);
      for (const child of gateForByLinkId.get(id) ?? []) queue.push(child);
    }
  }
  return hidden;
};
